import { useMemo } from 'react'
import styled, { DefaultTheme, useTheme } from 'styled-components'
import { BigNumber } from '@ethersproject/bignumber'
import { Box, Flex, FlexProps, Skeleton, Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { BetPosition, NodeRound, Round } from 'state/types'
import { useConfig } from 'views/Predictions/context/ConfigProvider'
import { formatUsdv2, formatBnbv2, getRoundPosition, getPriceDifference } from '../../helpers'
import { formatBnb, formatUsd } from '../History/helpers'
import PositionTag from '../PositionTag'
import { StyledRowBorders } from 'views/Swap/components/AdvancedSwapDetails'

// PrizePoolRow
interface PrizePoolRowProps extends FlexProps {
  totalAmount: NodeRound['totalAmount']
}

const getPrizePoolAmount = (totalAmount: PrizePoolRowProps['totalAmount'], displayedDecimals: number) => {
  if (!totalAmount) {
    return '0'
  }

  return formatBnbv2(totalAmount, displayedDecimals)
}

const Row = ({ children, ...props }) => {
  const theme = useTheme();
  return (
    <Flex alignItems="center" justifyContent="space-between" {...props}>
      {children}
    </Flex>
  )
}

export const PrizePoolRow: React.FC<React.PropsWithChildren<PrizePoolRowProps>> = ({ totalAmount, ...props }) => {
  const { t } = useTranslation()
  const { token, displayedDecimals } = useConfig()

  return (
    <Row {...props} my='20px' >
      <Text fontSize="18px" color='textSubtle'>{t('Prize Pool')}:</Text>
      <Text fontSize="18px">{`${getPrizePoolAmount(totalAmount, displayedDecimals)} ${token.symbol}`}</Text>
    </Row>
  )
}

// Payout Row
interface PayoutRowProps extends FlexProps {
  positionLabel: string
  multiplier: number
  amount: number
}

export const PayoutRow: React.FC<React.PropsWithChildren<PayoutRowProps>> = ({
  positionLabel,
  multiplier,
  amount,
  ...props
}) => {
  const { t } = useTranslation()
  const formattedMultiplier = `${multiplier.toLocaleString(undefined, { maximumFractionDigits: 2 })}x`
  const { token, displayedDecimals } = useConfig()

  return (
    <StyledRowBorders isSmall showBorderBottom showBorderTop={positionLabel === 'up'}>
      <Text color='textSubtle' fontSize="14px" textTransform="uppercase">
        {positionLabel}:
      </Text>
      <Flex alignItems="center">
        <Text fontSize="14px" lineHeight="14px" >
          {t('%multiplier% Payout', { multiplier: formattedMultiplier })}
        </Text>
        <Text mx="4px">|</Text>
        <Text fontSize="14px" lineHeight="14px">{`${formatBnb(amount, displayedDecimals)} ${token.symbol}`}</Text>
      </Flex>
    </StyledRowBorders>
  )
}

interface LockPriceRowProps extends FlexProps {
  lockPrice: NodeRound['lockPrice']
}

export const LockPriceRow: React.FC<React.PropsWithChildren<LockPriceRowProps>> = ({ lockPrice, ...props }) => {
  const { t } = useTranslation()
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()
  const theme = useTheme()

  return (
    <Row {...props} pb='10px' mb='-10px' style={{borderBottom: `1px solid ${theme.colors.cardBorder}`}}>
      <Text fontSize="18px">{t('Locked Price')}:</Text>
      <Text fontSize="18px">{formatUsdv2(lockPrice, minPriceUsdDisplayed, displayedDecimals)}</Text>
    </Row>
  )
}

// RoundResultBox
interface RoundResultBoxProps {
  betPosition?: BetPosition
  isNext?: boolean
  isLive?: boolean
  hasEntered?: boolean
}

const getBackgroundColor = ({
  theme,
  betPosition,
  isNext,
  isLive,
  hasEntered,
}: RoundResultBoxProps & { theme: DefaultTheme }) => {
  if (isNext) {
    return 'linear-gradient(180deg, #53DEE9 0%, #7645D9 100%)'
  }

  if (hasEntered || isLive) {
    return theme.colors.secondary
  }

  switch (betPosition) {
    case BetPosition.BULL:
      return theme.colors.success
    case BetPosition.BEAR:
      return theme.colors.failure
    case BetPosition.HOUSE:
      return theme.colors.textDisabled
    default:
      return theme.colors.cardBorder
  }
}

const Background = styled(Box)<RoundResultBoxProps>`
  border-radius: 16px;
  padding: 0px;
`

const StyledRoundResultBox = styled.div`
  border-radius: 14px;
  padding: 16px 0px;
`

export const RoundResultBox: React.FC<React.PropsWithChildren<RoundResultBoxProps>> = ({
  isNext = false,
  hasEntered = false,
  isLive = false,
  children,
  ...props
}) => {
  return (
    <Background isNext={isNext} hasEntered={hasEntered} isLive={isLive} {...props}>
      <StyledRoundResultBox>{children}</StyledRoundResultBox>
    </Background>
  )
}

interface RoundPriceProps {
  lockPrice: BigNumber
  closePrice: BigNumber
}

export const RoundPrice: React.FC<React.PropsWithChildren<RoundPriceProps>> = ({ lockPrice, closePrice }) => {
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()
  const betPosition = getRoundPosition(lockPrice, closePrice)
  const priceDifference = getPriceDifference(closePrice, lockPrice)

  const textColor = useMemo(() => {
    switch (betPosition) {
      case BetPosition.BULL:
        return 'success'
      case BetPosition.BEAR:
        return 'failure'
      case BetPosition.HOUSE:
      default:
        return 'textDisabled'
    }
  }, [betPosition])

  return (
    <Flex alignItems="center" justifyContent="space-between" mb="16px" height={'36px'}>
      {closePrice ? (
        <Text color={textColor} fontWeight={700} fontSize="18px" className='syncopate'>
          {formatUsdv2(closePrice, minPriceUsdDisplayed, displayedDecimals)}
        </Text>
      ) : (
        <Skeleton height="34px" my="1px" />
      )}
      <PositionTag betPosition={betPosition}>
        {formatUsdv2(priceDifference, minPriceUsdDisplayed, displayedDecimals)}
      </PositionTag>
    </Flex>
  )
}

/**
 * TODO: Remove
 *
 * This is a temporary function until we consolidate the data coming from the graph versus the node
 */
interface PrizePoolHistoryRowProps extends FlexProps {
  totalAmount: number
}

const getPrizePoolAmountHistory = (totalAmount: PrizePoolHistoryRowProps['totalAmount'], displayedDecimals: number) => {
  if (!totalAmount) {
    return '0'
  }

  return formatBnb(totalAmount, displayedDecimals)
}

export const PrizePoolHistoryRow: React.FC<React.PropsWithChildren<PrizePoolHistoryRowProps>> = ({
  totalAmount,
  ...props
}) => {
  const { t } = useTranslation()
  const { token, displayedDecimals } = useConfig()

  return (
    <StyledRowBorders isSmall showBorderBottom showBorderTop>
      <Text color='textSubtle' fontSize="14px" >{t('Prize Pool')}:</Text>
      <Text fontSize="14px" >{`${getPrizePoolAmountHistory(totalAmount, displayedDecimals)} ${token.symbol}`}</Text>
    </StyledRowBorders>
  )
}

interface LockPriceHistoryRowProps extends FlexProps {
  lockPrice: Round['lockPrice']
}

export const LockPriceHistoryRow: React.FC<React.PropsWithChildren<LockPriceHistoryRowProps>> = ({
  lockPrice,
  ...props
}) => {
  const { t } = useTranslation()
  const { displayedDecimals } = useConfig()
  const theme = useTheme()

  return (
    <StyledRowBorders isSmall showBorderBottom>
      <Text color='textSubtle' fontSize="14px">{t('Locked Price')}:</Text>
      <Text fontSize="14px">{formatUsd(lockPrice, displayedDecimals)}</Text>
    </StyledRowBorders>
  )
}
/**
 * END TEMPORARY COMPONENTS
 */
